import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Plus, GripVertical, Lock, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { Slider } from "../../components/ui/slider";
import { api, asset, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ProfileView, ProfileBackground } from "../../components/ProfileView";
import { SOCIAL, BADGE_ICON, BadgeIcon } from "../../components/Icons";
import { PageHead, Locked } from "./parts";

const FIELDS = ["display_name", "bio", "location", "layout", "accent_color", "avatar_url", "background_url", "music_url", "music_title", "bg_dim", "bg_blur", "card_opacity", "typewriter_bio", "glow", "show_views", "avatar_shape", "domain", "image_domain", "custom_badge", "links"];
export const pickProfile = (u) => Object.fromEntries(FIELDS.map((k) => [k, u[k]]));
const LAYOUTS = [{ id: "classic", d: "centered card" }, { id: "card", d: "wide card, button links" }, { id: "minimal", d: "no card, just type" }, { id: "split", d: "two columns" }];
const own = (u, k) => (u.purchases || []).includes(k);

const Row = ({ label, children, hint }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between"><span className="eyebrow">{label}</span>{hint && <span className="font-mono text-[10px] text-zinc-600">{hint}</span>}</div>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange, testid, disabled }) => (
  <label className="flex items-center justify-between rounded-xl border border-[#1f1f28] bg-[#0b0b0e] px-4 py-3 text-sm">
    <span className="flex items-center gap-2 text-zinc-300">{label}{disabled && <Lock className="h-3 w-3 text-[#ff3b91]" />}</span>
    <Switch data-testid={testid} checked={!!checked} disabled={disabled} onCheckedChange={onChange} className="data-[state=checked]:bg-[#ff3b91]" />
  </label>
);

const UploadBox = ({ kind, value, onDone, onClear, accept, label, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const go = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post(`/uploads?kind=${kind}`, fd); onDone(data.url, file.name); toast.success("Uploaded — remember to save"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      {kind !== "music" && <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-[#262630] bg-[#0b0b0e]">{value && <img src={asset(value)} alt="" className="h-full w-full object-cover" />}</div>}
      <input ref={ref} type="file" accept={accept} hidden onChange={(e) => go(e.target.files[0])} data-testid={`${testid}-file-input`} />
      <button type="button" onClick={() => ref.current.click()} className="btn-ghost flex-1" data-testid={`${testid}-upload-button`} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{value ? `replace ${label}` : `upload ${label}`}</button>
      {value && <button type="button" onClick={onClear} className="btn-ghost px-3" data-testid={`${testid}-clear-button`}><Trash2 className="h-4 w-4" /></button>}
    </div>
  );
};

const LinksEditor = ({ links, set, limit, meta }) => {
  const add = () => links.length < limit ? set([...links, { id: `tmp-${Date.now()}`, platform: "discord", url: "", label: "" }]) : toast.error(`Your plan allows ${limit} links`);
  const upd = (i, k, v) => set(links.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const move = (i, d) => { const n = [...links]; const t = n[i + d]; if (!t) return; n[i + d] = n[i]; n[i] = t; set(n); };
  return (
    <div className="space-y-3">
      {links.map((l, i) => (
        <div key={l.id} data-testid={`link-row-${i}`} className="space-y-2 rounded-xl border border-[#1f1f28] bg-[#0b0b0e] p-3">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => move(i, -1)} className="text-zinc-600 hover:text-white" title="move up"><GripVertical className="h-4 w-4" /></button>
            <select data-testid={`link-platform-select-${i}`} value={l.platform} onChange={(e) => upd(i, "platform", e.target.value)} className="field w-40 py-2">
              {meta.platforms.map((p) => <option key={p} value={p}>{SOCIAL[p]?.label || p}</option>)}
            </select>
            <input data-testid={`link-label-input-${i}`} className="field py-2" placeholder="label (optional)" value={l.label} maxLength={40} onChange={(e) => upd(i, "label", e.target.value)} />
            <button type="button" onClick={() => set(links.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400" data-testid={`link-remove-button-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <input data-testid={`link-url-input-${i}`} className="field py-2 font-mono text-xs" placeholder={l.platform === "email" ? "you@mail.com" : l.platform === "discord" ? "invite link or username" : "https://"} value={l.url} onChange={(e) => upd(i, "url", e.target.value)} />
        </div>
      ))}
      <button type="button" onClick={add} className="btn-ghost w-full" data-testid="add-link-button"><Plus className="h-4 w-4" /> add link <span className="font-mono text-[10px] text-zinc-500">{links.length}/{limit}</span></button>
    </div>
  );
};

export default function Customize() {
  const { user, setUser } = useAuth();
  const [d, setD] = useState(pickProfile(user));
  const [extra, setExtra] = useState({ badges: [], verified: user.verified, views: user.views });
  const [meta, setMeta] = useState({ platforms: Object.keys(SOCIAL), badge_icons: Object.keys(BADGE_ICON), free_link_limit: 5, premium_link_limit: 25 });
  const [domains, setDomains] = useState([]);
  const [saving, setSaving] = useState(false);
  const prem = user.is_premium;
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));

  const loadPreview = () => api.get("/me/preview").then(({ data }) => setExtra({ badges: data.badges, verified: data.verified, views: data.views }));
  useEffect(() => {
    loadPreview();
    api.get("/meta").then(({ data }) => setMeta(data));
    api.get("/domains").then(({ data }) => setDomains(data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...d, links: d.links.filter((l) => l.url.trim()).map((l) => ({ ...l, id: l.id?.startsWith("tmp-") ? null : l.id })) };
      const { data } = await api.put("/me/profile", payload);
      setUser(data); setD(pickProfile(data)); loadPreview();
      toast.success("Profile saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const ownBadge = own(user, "custom_badge");
  const badges = extra.badges.filter((b) => b.id !== "custom").concat(d.custom_badge?.name ? [{ id: "custom", ...d.custom_badge }] : []);
  const preview = { ...d, username: user.username, verified: extra.verified, views: extra.views, badges };

  return (
    <div>
      <PageHead eyebrow="customize" title="make it yours">
        <button onClick={save} disabled={saving} className="btn-pink" data-testid="customize-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}save changes</button>
      </PageHead>
      <div className="grid gap-8 xl:grid-cols-[1fr_440px]">
        <Tabs defaultValue="profile">
          <TabsList className="mb-6 h-auto flex-wrap justify-start gap-1 rounded-xl border border-[#1f1f28] bg-[#0b0b0e] p-1">
            {["profile", "appearance", "links", "music", "extras"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`customize-tab-${t}`} className="rounded-lg px-4 py-2 text-xs uppercase tracking-wider text-zinc-400 data-[state=active]:bg-[#ff3b91] data-[state=active]:text-white">{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="profile" className="space-y-6">
            <Row label="display name" hint={`${d.display_name.length}/32`}><input data-testid="display-name-input" className="field" maxLength={32} value={d.display_name} onChange={(e) => set("display_name", e.target.value)} /></Row>
            <Row label="bio" hint={`${d.bio.length}/300`}><textarea data-testid="bio-input" className="field min-h-[110px] resize-none" maxLength={300} value={d.bio} onChange={(e) => set("bio", e.target.value)} /></Row>
            <Row label="location"><input data-testid="location-input" className="field" maxLength={40} value={d.location} onChange={(e) => set("location", e.target.value)} placeholder="somewhere online" /></Row>
            <Row label="avatar"><UploadBox kind="avatar" label="avatar" testid="avatar" accept="image/*" value={d.avatar_url} onDone={(u) => set("avatar_url", u)} onClear={() => set("avatar_url", "")} /></Row>
            <Row label="background"><UploadBox kind="background" label="background" testid="background" accept="image/*" value={d.background_url} onDone={(u) => set("background_url", u)} onClear={() => set("background_url", "")} /></Row>
          </TabsContent>

          <TabsContent value="appearance" className="space-y-6">
            <Row label="layout">
              <div className="grid grid-cols-2 gap-3">
                {LAYOUTS.map((l) => {
                  const locked = !prem && !meta.free_layouts?.includes(l.id) && l.id !== "classic";
                  return (
                    <button key={l.id} type="button" data-testid={`layout-option-${l.id}`} onClick={() => locked ? toast.error("That layout requires Premium") : set("layout", l.id)}
                      className={`rounded-xl border p-4 text-left transition-all ${d.layout === l.id ? "border-[#ff3b91] bg-[#ff3b91]/10 shadow-[0_0_20px_rgba(255,59,145,.15)]" : "border-[#1f1f28] bg-[#0b0b0e] hover:border-[#ff3b91]/40"}`}>
                      <div className="flex items-center gap-2 font-display font-semibold capitalize">{l.id}{locked && <Lock className="h-3 w-3 text-[#ff3b91]" />}</div>
                      <div className="mt-1 text-xs text-zinc-500">{l.d}</div>
                    </button>
                  );
                })}
              </div>
            </Row>
            <Row label="accent colour" hint={!prem ? "premium" : ""}>
              <div className="flex items-center gap-3">
                <input type="color" data-testid="accent-color-input" disabled={!prem} value={d.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="h-11 w-14 cursor-pointer rounded-lg border border-[#262630] bg-transparent disabled:cursor-not-allowed disabled:opacity-40" />
                <span className="font-mono text-sm text-zinc-400">{d.accent_color}</span>
              </div>
            </Row>
            <Row label="avatar shape">
              <div className="flex gap-2">{["circle", "rounded", "square"].map((s) => <button key={s} type="button" data-testid={`avatar-shape-${s}`} onClick={() => set("avatar_shape", s)} className={`rounded-lg border px-4 py-2 text-xs capitalize transition-colors ${d.avatar_shape === s ? "border-[#ff3b91] text-white" : "border-[#262630] text-zinc-400 hover:text-white"}`}>{s}</button>)}</div>
            </Row>
            {[["bg_dim", "background dim", 0, 90], ["bg_blur", "background blur", 0, 20], ["card_opacity", "card opacity", 0, 100]].map(([k, l, mn, mx]) => (
              <Row key={k} label={l} hint={d[k]}><Slider data-testid={`slider-${k}`} min={mn} max={mx} step={1} value={[d[k]]} onValueChange={([v]) => set(k, v)} /></Row>
            ))}
            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle label="glow effects" checked={d.glow} onChange={(v) => set("glow", v)} testid="toggle-glow" />
              <Toggle label="show view count" checked={d.show_views} onChange={(v) => set("show_views", v)} testid="toggle-show-views" />
              <Toggle label="typewriter bio" checked={d.typewriter_bio} disabled={!prem} onChange={(v) => set("typewriter_bio", v)} testid="toggle-typewriter" />
            </div>
            {!prem && <Locked what="premium">Extra layouts, accent colours and typewriter bios are Premium.</Locked>}
          </TabsContent>

          <TabsContent value="links"><LinksEditor links={d.links} set={(v) => set("links", v)} meta={meta} limit={prem ? meta.premium_link_limit : meta.free_link_limit} /></TabsContent>

          <TabsContent value="music" className="space-y-6">
            {!prem ? <Locked what="premium">Profile music is a Premium feature.</Locked> : (<>
              <Row label="track"><UploadBox kind="music" label="track" testid="music" accept="audio/*" value={d.music_url} onDone={(u, name) => { set("music_url", u); if (!d.music_title) set("music_title", name.replace(/\.[^.]+$/, "").slice(0, 60)); }} onClear={() => set("music_url", "")} /></Row>
              {d.music_url && <div className="font-mono text-xs text-zinc-500" data-testid="music-uploaded-indicator">track attached ✓</div>}
              <Row label="track title"><input data-testid="music-title-input" className="field" maxLength={60} value={d.music_title} onChange={(e) => set("music_title", e.target.value)} /></Row>
            </>)}
          </TabsContent>

          <TabsContent value="extras" className="space-y-8">
            <Row label="custom badge">
              {!ownBadge ? <Locked what="custom badge" /> : (
                <div className="space-y-3 rounded-xl border border-[#1f1f28] bg-[#0b0b0e] p-4">
                  <input data-testid="custom-badge-name-input" className="field" maxLength={20} placeholder="badge name" value={d.custom_badge?.name || ""} onChange={(e) => set("custom_badge", { icon: "star", color: "#ff3b91", ...d.custom_badge, name: e.target.value })} />
                  <div className="flex flex-wrap gap-2">
                    {meta.badge_icons.map((ic) => (
                      <button key={ic} type="button" data-testid={`custom-badge-icon-${ic}`} onClick={() => set("custom_badge", { name: "", color: "#ff3b91", ...d.custom_badge, icon: ic })} className={`rounded-lg border p-2 transition-colors ${d.custom_badge?.icon === ic ? "border-[#ff3b91] bg-[#ff3b91]/10" : "border-[#262630] hover:border-[#ff3b91]/50"}`}>
                        <BadgeIcon icon={ic} className="h-4 w-4" style={{ color: d.custom_badge?.color || "#ff3b91" }} />
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3"><input type="color" data-testid="custom-badge-color-input" value={d.custom_badge?.color || "#ff3b91"} onChange={(e) => set("custom_badge", { name: "", icon: "star", ...d.custom_badge, color: e.target.value })} className="h-10 w-12 rounded-lg border border-[#262630] bg-transparent" /><span className="text-xs text-zinc-500">badge colour</span></div>
                </div>
              )}
            </Row>
            <Row label="profile domain">
              {!own(user, "custom_domain") ? <Locked what="custom domain" /> : (
                <select data-testid="domain-select" className="field" value={d.domain} onChange={(e) => set("domain", e.target.value)}>
                  <option value="">visc.lol (default)</option>
                  {domains.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              )}
            </Row>
          </TabsContent>
        </Tabs>

        <div className="xl:sticky xl:top-8 xl:self-start">
          <div className="eyebrow mb-3 flex items-center justify-between"><span>live preview</span><span className="font-mono normal-case tracking-normal text-zinc-600">{d.domain || "visc.lol"}/{user.username}</span></div>
          <div data-testid="dashboard-profile-preview-card" className="relative flex min-h-[620px] items-center justify-center overflow-hidden rounded-3xl border border-[#1f1f28] p-5">
            <ProfileBackground p={preview} fixed={false} />
            <div className="relative w-full origin-center scale-[0.9]"><ProfileView profile={preview} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
